import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The parts of the signed-out page that carry its atmosphere, kept out of
 * LandingPage so the page itself stays readable as a document.
 *
 * Three directions were built and compared here behind a `?stilius=` parameter;
 * this is the one that was chosen, and the other two are in the history rather
 * than in the file. A page carrying three designs is a page nobody can change.
 */

/**
 * The figures are the same `/svg/` generators the questions are drawn with, and
 * their aspect ratios are nothing alike — a number line is 6.6:1 and a clock is
 * 1:1. Six equal squares is what turned the number line into a hairline and made
 * the set read as clip art, so each figure is given a tile its own shape fits.
 */
const FIGURES = [
  {
    src: "/svg/protractor?degrees=60&show_question=true",
    alt: "Kampas ant matlankio",
    topic: "Kampai",
    span: "lg:col-span-4",
    height: "h-56 sm:h-64",
  },
  {
    src: "/svg/clock?hour=3&minute=30",
    alt: "Laikrodis, rodantis 3.30",
    topic: "Laikas",
    span: "lg:col-span-2",
    height: "h-56 sm:h-64",
  },
  {
    src: "/svg/number-line?min=0&max=10&step=1&markers=A:4",
    alt: "Skaičių tiesė su pažymėtu tašku",
    topic: "Skaičių tiesė",
    span: "sm:col-span-2 lg:col-span-6",
    height: "h-24 sm:h-28",
  },
  {
    src: "/svg/triangle?side_a=3&side_b=4&side_c=5&show_edge_lengths=true",
    alt: "Statusis trikampis, kraštinės 3, 4 ir 5",
    topic: "Trikampiai",
    span: "lg:col-span-2",
    height: "h-44 sm:h-48",
  },
  {
    src: "/svg/fraction-bar?numerator=3&denominator=4",
    alt: "Trupmena trys ketvirtadaliai",
    topic: "Trupmenos",
    span: "lg:col-span-2",
    height: "h-44 sm:h-48",
  },
  {
    // Coloured from the palette rather than the generator's defaults, whose
    // red/green/blue is the one thing on this page that belongs to no brand.
    src: "/svg/bar-chart?values=Pirmadienis:12:%231F5FD8,Antradienis:18:%23C67C05,Tre%C4%8Diadienis:9:%236F9BE8&y_axis_title=Kiekis&show_values=false",
    alt: "Stulpelinė diagrama",
    topic: "Diagramos",
    span: "lg:col-span-2",
    height: "h-44 sm:h-48",
  },
];

/* ------------------------------------------------------------------ hero --- */

/** Backdrops run the full width of the window, not of the 6xl column — a tinted
 *  band that stops short of the edges is a rectangle, not an atmosphere. And
 *  they fade out on every edge, so nothing has a seam. */
const BACKDROP = "pointer-events-none absolute left-1/2 -z-10 w-screen -translate-x-1/2";

export function HeroBackdrop() {
  return (
    <div
      aria-hidden="true"
      className={cn(BACKDROP, "-top-24 h-[680px] overflow-hidden")}
      style={{
        WebkitMaskImage: "linear-gradient(to bottom, transparent, black 18%, black 55%, transparent 95%)",
        maskImage: "linear-gradient(to bottom, transparent, black 18%, black 55%, transparent 95%)",
      }}
    >
      <div className="absolute top-0 left-[6%] h-[30rem] w-[30rem] rounded-full bg-primary/40 blur-[110px]" />
      <div className="absolute top-16 right-[8%] h-96 w-96 rounded-full bg-brand-accent/25 blur-[110px]" />
      <div className="absolute top-64 left-1/3 h-72 w-[40rem] rounded-full bg-primary/20 blur-[120px]" />
    </div>
  );
}

/** The eyebrow above the headline. */
export function HeroEyebrow() {
  return (
    <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm ring-1 ring-border">
      <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
      Matematika 1–9 klasei
    </p>
  );
}

/* ----------------------------------------------------------------- stats --- */

export function StatRow({ stats }: { stats: { value: string; label: string }[] }) {
  return (
    <dl className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="rounded-2xl bg-gradient-to-b from-card to-secondary p-4 shadow-sm shadow-primary/5 ring-1 ring-border/70 sm:p-5"
        >
          <dt className="bg-gradient-to-br from-foreground to-primary bg-clip-text text-2xl font-bold tabular-nums text-transparent sm:text-3xl">
            {stat.value}
          </dt>
          <dd className="mt-0.5 text-sm text-muted-foreground">{stat.label}</dd>
        </div>
      ))}
    </dl>
  );
}

/* --------------------------------------------------------------- pillars --- */

export function PillarCard({
  icon: Icon,
  title,
  body,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-3xl bg-gradient-to-b from-card to-secondary p-5 shadow-sm shadow-primary/5 ring-1 ring-border/70 transition duration-300 hover:-translate-y-0.5 hover:shadow-md sm:p-6">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15"
        aria-hidden="true"
      >
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="mt-3 text-lg font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

/* --------------------------------------------------------------- figures --- */

export function FigureGallery() {
  return (
    <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
      {FIGURES.map((figure) => (
        <figure
          key={figure.src}
          className={cn(
            "group relative overflow-hidden rounded-3xl bg-gradient-to-br from-card via-card to-secondary p-4 shadow-sm shadow-primary/5 ring-1 ring-border/70 transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10",
            figure.span,
          )}
        >
          <div className={cn("flex items-center justify-center", figure.height)}>
            <img
              src={figure.src}
              alt={figure.alt}
              className="max-h-full max-w-full transition-transform duration-300 group-hover:scale-[1.04]"
            />
          </div>
          <figcaption className="absolute bottom-3 left-3 rounded-full bg-background/70 px-2.5 py-1 text-[11px] font-medium text-muted-foreground opacity-0 backdrop-blur transition duration-300 group-hover:opacity-100">
            {figure.topic}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
